import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Icon, IconName } from "./icons/Icon";
import { Spinner } from "./ui/spinner";
import { useEventTracking } from "hooks/useEventTracking";
import { cn } from "lib/utils";
import { MODAL_SURFACE_CLASS } from "./ui/modal-styles";

export interface PopupProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose: () => void;
  size?: "compact" | "default" | "wide";
  contentLayout?: "centered" | "form";
  actionsLayout?: "center" | "end" | "stretch" | "stack";
  header?: string;
  title: string;
  description?: string;
  buttonText?: string;
  onClickButton?: () => void;
  actions?: React.ReactNode;
  isSubmitting: boolean;
  isButtonDisabled?: boolean;
  icon?: IconName;
  iconSize?: number;
}

const FADE_DURATION_MS = 220;
const POPUP_SIZE_CLASS: Record<NonNullable<PopupProps["size"]>, string> = {
  compact: "w-[calc(100vw-2rem)] max-w-[22rem] sm:w-[calc(100vw-4rem)] sm:max-w-sm",
  default: "w-[calc(100vw-2rem)] max-w-[24rem] sm:w-[calc(100vw-4rem)] sm:max-w-md",
  wide: "w-[calc(100vw-2rem)] max-w-[28rem] sm:w-[calc(100vw-4rem)] sm:max-w-[35rem]",
};
const POPUP_CONTENT_LAYOUT_CLASS: Record<NonNullable<PopupProps["contentLayout"]>, string> = {
  centered: "",
  form: "w-full items-stretch",
};
const POPUP_ACTIONS_LAYOUT_CLASS: Record<NonNullable<PopupProps["actionsLayout"]>, string> = {
  center: "justify-center",
  end: "justify-end",
  stretch: "items-stretch",
  stack: "flex-col items-stretch gap-2",
};

export interface PopupHandle {
  close: () => void;
}

const Popup = React.forwardRef<PopupHandle, PopupProps>(
  (
    {
      className,
      onClose,
      size = "default",
      contentLayout = "centered",
      actionsLayout = "center",
      title,
      header,
      description,
      buttonText,
      onClickButton,
      actions,
      isSubmitting,
      isButtonDisabled,
      icon,
      iconSize,
      children,
      ...props
    },
    ref
  ) => {
    const { track } = useEventTracking();
    const startTimeRef = useRef<number>(Date.now());
    const hasTrackedRef = useRef(false);
    const skipNextTrackingRef = useRef(process.env.NODE_ENV !== "production");
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const openFrameRef = useRef<number | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const trackPopupOnce = useCallback(() => {
      if (hasTrackedRef.current) {
        return;
      }

      hasTrackedRef.current = true;
      const durationMs = Date.now() - startTimeRef.current;
      track("popup", { title, timeSpentMs: durationMs });
    }, [title, track]);

    const handleCleanupTrack = useCallback(() => {
      if (hasTrackedRef.current) {
        return;
      }

      if (skipNextTrackingRef.current) {
        skipNextTrackingRef.current = false;
        return;
      }

      trackPopupOnce();
    }, [trackPopupOnce]);

    const handleClose = useCallback(() => {
      if (isClosing) {
        return;
      }

      trackPopupOnce();
      setIsClosing(true);
      setIsVisible(false);

      closeTimerRef.current = setTimeout(() => {
        onClose();
        closeTimerRef.current = null;
      }, FADE_DURATION_MS);
    }, [isClosing, onClose, trackPopupOnce]);

    useImperativeHandle(
      ref,
      () => ({
        close: handleClose,
      }),
      [handleClose]
    );

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (!nextOpen) {
          handleClose();
        }
      },
      [handleClose],
    );

    useEffect(() => {
      startTimeRef.current = Date.now();
      openFrameRef.current = requestAnimationFrame(() => {
        setIsVisible(true);
      });

      let skipResetTimer: ReturnType<typeof setTimeout> | undefined;
      if (skipNextTrackingRef.current) {
        skipResetTimer = setTimeout(() => {
          skipNextTrackingRef.current = false;
        }, 0);
      }

      return () => {
        if (skipResetTimer) {
          clearTimeout(skipResetTimer);
        }
        if (openFrameRef.current !== null) {
          cancelAnimationFrame(openFrameRef.current);
          openFrameRef.current = null;
        }
        handleCleanupTrack();
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
      };
    }, [handleCleanupTrack]);

    const handleContentInteractOutside = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-popup-close-ignore="true"]')) {
        event.preventDefault();
      }
    };

    const overlay = (
      <Dialog open onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            "z-[500] !w-auto sm:!w-auto max-w-none justify-items-center overflow-visible border-0 bg-transparent p-0 shadow-none backdrop-blur-sm [&>button]:hidden data-[state=open]:animate-none data-[state=closed]:animate-none",
            isClosing ? "pointer-events-none" : "",
            className,
          )}
          onInteractOutside={handleContentInteractOutside}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            handleClose();
          }}
          {...props}
        >
          <Card
            variant="white"
            disableAnimation
            className={cn(
              "wf-popup-scrollbar relative max-h-[calc(100dvh-3rem)] overflow-y-auto p-6 transition-[opacity,transform] ease-out sm:max-h-[calc(100dvh-4rem)] sm:p-8",
              POPUP_SIZE_CLASS[size],
              MODAL_SURFACE_CLASS,
              isClosing ? "duration-200" : "duration-300",
              isVisible && !isClosing ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.98] opacity-0",
            )}
            data-popup-close-ignore="true"
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
              data-popup-close-ignore="true"
            >
              <Icon name="greyX" size={16}/>
            </button>
            {icon != null && <div className="flex justify-center">
              <Icon name={icon!} size={iconSize ?? 32} />
            </div>
            }
            {header && <CardHeader className="items-center text-[length:var(--body-XS-font-size)]">{header}</CardHeader>}
            <CardTitle centered={true} size="small" className="py-2">
              <DialogTitle asChild>
                <h1 className="text-heading font-headings-h3 font-[var(--headings-h3-font-weight)] text-[length:var(--headings-h4-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--headings-h5-line-height)] [font-style:var(--headings-h3-font-style)] whitespace-pre-line">
                  {typeof title === "string" ? title.replace(/\\n/g, "\n") : title}
                </h1>
              </DialogTitle>
              {description ? (
                <DialogDescription className="text-muted-foreground font-body-s font-[var(--body-s-font-weight)] text-[0.7rem] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)] mb-2">
                  {description}
                </DialogDescription>
              ) : null}
            </CardTitle>
            <CardContent className={POPUP_CONTENT_LAYOUT_CLASS[contentLayout]}>
              {children}
              {(actions || (buttonText && onClickButton)) ? (
                <div className={cn("mt-1 flex w-full", POPUP_ACTIONS_LAYOUT_CLASS[actionsLayout])}>
                  {actions || (
                    <Button
                      onClick={onClickButton}
                      size="auto"
                      disabled={isSubmitting || isButtonDisabled}
                      className={actionsLayout === "stretch" || actionsLayout === "stack" ? "w-full" : ""}
                    >
                      {isSubmitting ? (
                        <span className="inline-flex items-center justify-center px-1 py-0.5">
                          <Spinner size="sm" />
                        </span>
                      ) : buttonText}
                    </Button>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    );

    return overlay;
  }
);

Popup.displayName = "Popup";

export default Popup;
